import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Button,
  Avatar,
  Paper,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HistoryIcon from "@mui/icons-material/History";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ScrollableTableContainer from "../../common/ScrollableTableContainer";

import { useStatus } from "../../../popup/NotificationContext";

import {
  useGetMyWalletQuery,
  useAllocateCreditsMutation,
  useTopupCreditsMutation,
  useGetMyTransactionsQuery,
  useGetAllocatableUsersQuery,
} from "../../../redux/admin/creditsApi";

const ROLE_LABELS = {
  admin: "Super Admin",
  Manager: "manager",
  sub_admin: "Admin",
  group_admin: "Dealer",
  client: "Client",
  user: "User",
  viewer: "Viewer",
  field_emp: "Field Employee",
  office_emp: "Office Employee",
  HR: "hr",
};

const isAdmin = (role) => role === "admin";

const formatMoney = (n) => {
  const num = Number(n || 0);
  return num.toLocaleString("en-IN");
};

const initials = (name = "") => {
  const s = String(name || "").trim();
  if (!s) return "U";
  const parts = s.split(" ").filter(Boolean);
  const a = parts[0]?.[0] || "U";
  const b = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (a + b).toUpperCase();
};

const txTypeLabel = (type) => {
  switch (type) {
    case "topup":
      return "Topup";
    case "allocate":
      return "Allocate";
    case "adjust":
      return "Adjust";
    case "reverse":
      return "Reverse";
    case "deduct":
      return "Deduct";
    default:
      return type || "-";
  }
};

const txTypeColor = (type) => {
  if (type === "topup") return "success";
  if (type === "allocate") return "primary";
  if (type === "deduct") return "warning";
  if (type === "reverse") return "info";
  if (type === "adjust") return "secondary";
  return "default";
};

export default function CreditsTransfer() {
  const { showStatus } = useStatus();
  const currentUser = useSelector((s) => s.auth.user);

  // Wallet
  const {
    data: walletData,
    isLoading: walletLoading,
    refetch: refetchWallet,
    isFetching: walletFetching,
  } = useGetMyWalletQuery();

  // Transactions
  const {
    data: transactionsData = [],
    isLoading: txLoading,
    refetch: refetchTx,
    isFetching: txFetching,
  } = useGetMyTransactionsQuery(undefined, { pollingInterval: 15000 });

  // Allocatable users (backend role-based)
  const {
    data: allocatableUsers = [],
    isLoading: usersLoading,
    refetch: refetchUsers,
    isFetching: usersFetching,
    error: usersError,
  } = useGetAllocatableUsersQuery(undefined, {
    // if role not supported backend returns 403 → show message
    refetchOnMountOrArgChange: true,
  });

  const [allocateCredits, { isLoading: allocating }] = useAllocateCreditsMutation();
  const [topupCredits, { isLoading: toppingUp }] = useTopupCreditsMutation();

  const balance = walletData?.balance ?? 0;

  // UI state
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [lastAllocation, setLastAllocation] = useState(null);

  // Topup dialog
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");

  const filteredUsers = useMemo(() => {
    const list = Array.isArray(allocatableUsers) ? allocatableUsers : [];
    return list.filter((u) => {
      const q = search.trim().toLowerCase();
      const matchesSearch =
        !q ||
        u.username?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q);

      const matchesRole = roleFilter === "all" || u.role === roleFilter;

      return matchesSearch && matchesRole;
    });
  }, [allocatableUsers, search, roleFilter]);

  const maxAllowed = isAdmin(currentUser?.role) ? Infinity : Number(balance || 0);
  const numericAmount = Number(amount || 0);
  const amountTooHigh = !isAdmin(currentUser?.role) && numericAmount > maxAllowed;

  const roleTitle = isAdmin(currentUser?.role) ? "Vault Control" : "Credit Ledger";
  const roleSubtitle = isAdmin(currentUser?.role)
    ? "System master wallet — topup & distribute credits safely."
    : `You can distribute credits up to your available balance (${formatMoney(balance)}).`;

  const canSubmit = !!selectedUser && numericAmount > 0 && !amountTooHigh && !allocating;

  const doRefresh = async () => {
    refetchWallet();
    refetchTx();
    refetchUsers();
    showStatus("info", "Refreshing data…");
  };

  const handleAllocate = async (e) => {
    e?.preventDefault();

    if (!selectedUser) return showStatus("warning", "Please select a recipient.");
    if (!numericAmount || numericAmount <= 0) return showStatus("warning", "Enter a valid amount.");

    if (!isAdmin(currentUser?.role) && numericAmount > balance) {
      return showStatus("error", `Insufficient balance. You have ${formatMoney(balance)}.`);
    }

    try {
      const allocationRecipient = selectedUser;
      await allocateCredits({
        toUserId: allocationRecipient._id,
        amount: numericAmount,
        note: note || `${currentUser?.role} Allocation`,
      }).unwrap();

      setLastAllocation({
        amount: numericAmount,
        recipient: allocationRecipient.username || allocationRecipient.email || "recipient",
      });
      window.dispatchEvent(new Event("credits:changed"));
      showStatus("success", `✅ ${formatMoney(numericAmount)} credits sent to ${allocationRecipient.username}`);
      setAmount("");
      setNote("");
      setSelectedUser(null);
      refetchWallet();
      refetchTx();
    } catch (err) {
      showStatus("error", err?.data?.msg || "Allocation failed.");
    }
  };

  const handleTopup = async () => {
    const n = Number(topupAmount || 0);
    if (!n || n <= 0) return showStatus("warning", "Enter a valid topup amount.");

    try {
      await topupCredits({ amount: n, note: "System Vault Topup" }).unwrap();
      showStatus("success", `✅ Vault topped up by ${formatMoney(n)} credits.`);
      setTopupAmount("");
      setTopupOpen(false);
      refetchWallet();
      refetchTx();
    } catch (err) {
      showStatus("error", err?.data?.msg || "Topup failed.");
    }
  };

  // Some roles will get 403 for allocatable endpoint -> show guidance
  const allocatableBlocked =
    usersError?.status === 403 ||
    (usersError?.data?.msg && String(usersError.data.msg).toLowerCase().includes("not authorized"));

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            {roleTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {roleSubtitle}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
            <Chip
              icon={<VerifiedUserIcon />}
              label={`${ROLE_LABELS[currentUser?.role] || currentUser?.role || "Role"}`}
              variant="outlined"
            />
            {!isAdmin(currentUser?.role) && (
              <Chip
                icon={<AccountBalanceWalletIcon />}
                label={`Available: ${formatMoney(balance)}`}
                color="primary"
                variant="outlined"
              />
            )}
          </Stack>
        </Box>

        {/* Wallet Card */}
        <Card
          sx={{
            minWidth: { xs: "100%", md: 360 },
            borderRadius: 3,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {(walletFetching || walletLoading) && (
            <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0 }} />
          )}
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="overline" color="text.secondary">
                  Wallet Balance
                </Typography>
                <Typography variant="h4" fontWeight={900} sx={{ lineHeight: 1.1 }}>
                  {walletLoading ? "…" : formatMoney(balance)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Received: {formatMoney(walletData?.totalReceived)} • Total Allocated:{" "}
                  {formatMoney(walletData?.totalAllocated)}
                </Typography>
              </Stack>

              <Stack spacing={1} alignItems="flex-end">
                <Tooltip title="Refresh">
                  <IconButton onClick={doRefresh}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>

                {isAdmin(currentUser?.role) && (
                  <Button
                    onClick={() => setTopupOpen(true)}
                    startIcon={<AddIcon />}
                    variant="contained"
                    sx={{ borderRadius: 2 }}
                  >
                    Topup Vault
                  </Button>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {lastAllocation && (
        <Alert
          severity="success"
          variant="outlined"
          icon={<VerifiedUserIcon />}
          sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}
        >
          Allocation confirmed: {formatMoney(lastAllocation.amount)} license credits were assigned to {lastAllocation.recipient}. Their available-credit balance updates automatically.
        </Alert>
      )}

      {/* Body Grid */}
      <Grid container spacing={2}>
        {/* Left: Transfer */}
        <Grid item xs={12} lg={7}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Stack spacing={0.2}>
                  <Typography variant="h6" fontWeight={800}>
                    Authorize Allocation
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select eligible recipient (backend role-based), enter amount and confirm.
                  </Typography>
                </Stack>
                <Chip icon={<SendIcon />} label="Secure Transfer" color="primary" variant="outlined" />
              </Stack>

              <Divider sx={{ my: 2 }} />

              {allocatableBlocked ? (
                <Alert severity="warning">
                  Your role is not allowed to allocate credits (or no eligible users found by backend rules).
                </Alert>
              ) : null}

              {/* Search + Filters */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mb: 1.5 }}
              >
                <TextField
                  fullWidth
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by username or email…"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  sx={{ minWidth: { xs: "100%", sm: 200 } }}
                  size="medium"
                >
                  <MenuItem value="all">All roles</MenuItem>
                  {/* show only roles present */}
                  {Array.from(new Set((allocatableUsers || []).map((u) => u.role)))
                    .filter(Boolean)
                    .map((r) => (
                      <MenuItem key={r} value={r}>
                        {ROLE_LABELS[r] || r}
                      </MenuItem>
                    ))}
                </Select>
              </Stack>

              {/* Users list */}
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  mb: 2,
                }}
              >
                {(usersFetching || usersLoading) && <LinearProgress />}

                <Box
                  sx={{
                    maxHeight: 320,
                    overflowY: "auto",
                  }}
                >
                  {usersLoading ? (
                    <Stack spacing={1} sx={{ p: 2 }}>
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} variant="rounded" height={52} />
                      ))}
                    </Stack>
                  ) : filteredUsers.length === 0 ? (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        No eligible users found.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack>
                      {filteredUsers.map((u) => {
                        const selected = selectedUser?._id === u._id;
                        return (
                          <Box
                            key={u._id}
                            onClick={() => setSelectedUser(u)}
                            sx={{
                              px: 2,
                              py: 1.3,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1.5,
                              borderBottom: "1px solid",
                              borderColor: "divider",
                              bgcolor: selected ? "action.selected" : "transparent",
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                          >
                            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                              <Avatar sx={{ width: 36, height: 36 }}>
                                {initials(u.username)}
                              </Avatar>

                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body1" fontWeight={700} noWrap>
                                  {u.username || "Unnamed"}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.2 }}>
                                  <Chip
                                    size="small"
                                    label={ROLE_LABELS[u.role] || u.role || "role"}
                                    variant="outlined"
                                  />
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {u.email || "-"}
                                  </Typography>
                                </Stack>
                              </Box>
                            </Stack>

                            <Chip
                              size="small"
                              label={`Balance: ${formatMoney(u?.credits?.balance || 0)}`}
                              color={selected ? "primary" : "default"}
                              variant={selected ? "filled" : "outlined"}
                            />
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Paper>

              {/* Selected user hint */}
              {selectedUser ? (
                <Alert
                  icon={<VerifiedUserIcon />}
                  severity="info"
                  sx={{ borderRadius: 2, mb: 2 }}
                >
                  Assigning to <b>{selectedUser.username}</b> ({ROLE_LABELS[selectedUser.role] || selectedUser.role})
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
                  Select a recipient to continue.
                </Alert>
              )}

              {/* Form */}
              <Box component="form" onSubmit={handleAllocate}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={`Amount ${!isAdmin(currentUser?.role) ? `(Max: ${formatMoney(balance)})` : ""}`}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      disabled={!selectedUser}
                      error={amountTooHigh}
                      helperText={
                        amountTooHigh
                          ? "Amount exceeds your available balance."
                          : "Enter the amount to transfer."
                      }
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Remarks (optional)"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      disabled={!selectedUser}
                      placeholder="e.g. Monthly allocation"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={<SendIcon />}
                      disabled={!canSubmit}
                      sx={{ borderRadius: 2, py: 1.2 }}
                      fullWidth
                    >
                      {allocating ? "Processing…" : "Confirm Allocation"}
                    </Button>

                    {!isAdmin(currentUser?.role) && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        Non-admin transfers deduct from your wallet (backend enforced).
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: History */}
        <Grid item xs={12} lg={5}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Stack spacing={0.2}>
                  <Typography variant="h6" fontWeight={800}>
                    Transaction History
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recent transactions (auto refresh every 15s).
                  </Typography>
                </Stack>

                <Tooltip title="Refresh">
                  <IconButton onClick={() => refetchTx()}>
                    <HistoryIcon />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Divider sx={{ my: 2 }} />

              {(txFetching || txLoading) && <LinearProgress sx={{ mb: 1 }} />}

              {txLoading ? (
                <Stack spacing={1}>
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={44} />
                  ))}
                </Stack>
              ) : transactionsData?.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No transactions yet.
                </Alert>
              ) : (
                <ScrollableTableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 2, overflow: "hidden" }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>To / From</TableCell>
                        <TableCell sx={{ fontWeight: 800 }} align="right">
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {transactionsData.slice(0, 10).map((tx) => {
                        const toName = tx?.toUser?.username || tx?.toUser?.email || "-";
                        const fromName = tx?.fromUser?.username || tx?.fromUser?.email || "-";

                        const counterpart =
                          tx?.type === "topup"
                            ? "System Vault"
                            : tx?.fromUser?._id === currentUser?._id
                            ? `To: ${toName}`
                            : `From: ${fromName}`;

                        return (
                          <TableRow key={tx._id}>
                            <TableCell>
                              <Chip
                                size="small"
                                label={txTypeLabel(tx.type)}
                                color={txTypeColor(tx.type)}
                                variant="outlined"
                              />
                              <Typography variant="caption" color="text.secondary" display="block">
                                {tx?.createdAt ? new Date(tx.createdAt).toLocaleString() : "-"}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontWeight={700}>
                                {counterpart}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {tx?.note || "-"}
                              </Typography>
                            </TableCell>

                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                fontWeight={900}
                                color={tx?.type === "topup" ? "success.main" : "text.primary"}
                              >
                                {formatMoney(tx.amount)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollableTableContainer>
              )}
            </CardContent>
          </Card>

          {/* Small footer hints */}
          <Card sx={{ borderRadius: 3, mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                Notes
              </Typography>
              <Stack spacing={0.7}>
                <Typography variant="body2" color="text.secondary">
                  • Eligible recipients are controlled by backend role rules (`CREDIT_ALLOC_RULES`).
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Parent-chain anti-fraud check is enforced backend-side.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Admin topup & adjustments are restricted backend-side.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Admin Topup Dialog */}
      <Dialog open={topupOpen} onClose={() => setTopupOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900 }}>Vault Topup</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Inject credits into the system vault (Admin only).
          </Typography>

          <TextField
            fullWidth
            label="Topup Amount"
            type="number"
            value={topupAmount}
            onChange={(e) => setTopupAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTopupOpen(false)} variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleTopup}
            variant="contained"
            startIcon={<AddIcon />}
            disabled={toppingUp || !Number(topupAmount)}
            sx={{ borderRadius: 2 }}
          >
            {toppingUp ? "Processing…" : "Confirm Topup"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
